import React, { createContext, useContext, useState, useCallback } from 'react';
import FlashMessage from '../components/FlashMessage';

const FlashMessageContext = createContext();

export const useFlashMessage = () => {
  const context = useContext(FlashMessageContext);
  if (!context) {
    throw new Error('useFlashMessage must be used within a FlashMessageProvider');
  }
  return context;
};

export const FlashMessageProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);

  const removeMessage = useCallback((id) => {
    setMessages(prev => prev.filter(msg => msg.id !== id));
  }, []);

  const addMessage = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const newMessage = { id, message, type, duration };
    
    setMessages(prev => [...prev, newMessage]);
    
    // Auto-remove message after duration + animation time
    if (duration > 0) {
      setTimeout(() => {
        removeMessage(id);
      }, duration + 300);
    }
    
    return id;
  }, [removeMessage]);

  const showSuccess = useCallback((message, duration = 5000) => {
    return addMessage(message, 'success', duration);
  }, [addMessage]);

  const showError = useCallback((message, duration = 7000) => {
    return addMessage(message, 'error', duration);
  }, [addMessage]);

  const showWarning = useCallback((message, duration = 6000) => {
    return addMessage(message, 'warning', duration);
  }, [addMessage]);

  const showInfo = useCallback((message, duration = 5000) => {
    return addMessage(message, 'info', duration);
  }, [addMessage]);

  const clearAll = useCallback(() => {
    setMessages([]);
  }, []);

  const value = {
    addMessage,
    removeMessage,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearAll,
    messages
  };

  return (
    <FlashMessageContext.Provider value={value}>
      {children}
      <div className="flash-message-container">
        {messages.map((msg) => (
          <FlashMessage
            key={msg.id}
            message={msg.message}
            type={msg.type}
            duration={0} // We handle duration in the provider
            onDismiss={() => removeMessage(msg.id)}
            show={true}
          />
        ))}
      </div>
    </FlashMessageContext.Provider>
  );
};
